import { Box, FormControlLabel, Grid, Switch } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useLazyGetProductQuery } from '../../../app/redux-fetch/apiProducts'
import { useGetCurrenciesByCustomerIdQuery } from '../../../app/redux-fetch/apiOrders'
import { convertToLabelValue } from '../../../app/utils'
import ActionButton from '../../../components/Buttons/ActionButton'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import InfoDisplay from '../../../components/Forms/InfoDisplay'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { getActivePriceInfo } from '../../Products/utils'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useProductModal } from '../hooks/useProductModal'
import LoadContentTabs from './LoadContent/LoadContentTabs'
import messages from './messages'

interface ProductFormProps {
  consignmentIndex: number
  packagingIndex: number
  productIndex: number
  removeProduct: (index: number) => void
  disabled?: boolean
  productsCount: number
}

const ProductForm: React.FC<ProductFormProps> = ({
  consignmentIndex,
  packagingIndex,
  productIndex,
  removeProduct,
  productsCount,
}) => {
  const { control, setValue, watch, unregister, getValues } = useFormContext()
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()
  const [fetchProduct, productResult] = useLazyGetProductQuery()

  const tenantId = watch('tenantId')
  const customerId = watch('customerId')

  const { ModalComponent, productOptions, products } = useProductModal({
    onSave: (id) => {
      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productDescription`,
        id
      )
    },
    customerId: customerId ?? 0,
  })

  const currencyId = watch(`bookings.${consignmentIndex}.currencyId`)

  const { data: currencies = [] } = useGetCurrenciesByCustomerIdQuery({
    customerId: customerId ?? 0,
    hideDisabled: true,
  })
  const { data: countries = [] } = useGetCountriesQuery()

  const useProduct = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.useProduct`,
    false
  )

  const isCountryOfOriginRequired =
    watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.isCrossBorder`) || useProduct

  const productId = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productId`
  )

  const quantity = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.quantity`,
    1
  )
  const unitPrice = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.unitPrice`,
    0
  )

  const total = useMemo(() => quantity * unitPrice, [quantity, unitPrice, productId])

  useEffect(() => {
    if (productResult.isUninitialized && productId) {
      fetchProduct({ tenantId, customerId, productId: +productId })
    }
  }, [
    useProduct,
    productId,
    products,
    consignmentIndex,
    packagingIndex,
    productIndex,
    productResult,
  ])

  if (variant === 'summary') {
    const totalValue = quantity * unitPrice

    return (
      <Box
        sx={{
          border: '1px solid #ccc',
          padding: '20px 16px 16px 16px',
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette?.accordion?.parent?.bg,
        }}
      >
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={3}>
            <InfoDisplayItem
              label={formatMessage(messages.productDescription)}
              value={
                watch(
                  `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productDescription`
                ) || 'N/A'
              }
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.quantity)}
              value={
                watch(
                  `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.quantity`
                ) || 'N/A'
              }
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.unitPrice)}
              value={
                watch(
                  `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.unitPrice`
                ) || 'N/A'
              }
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem label={'Total'} value={totalValue || 'N/A'} isSmallScreen={false} />
          </Grid>
          <Grid item xs={12} md={3}>
            <InfoDisplayItem
              label={formatMessage(messages.countryOfOrigin)}
              value={
                watch(
                  `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.countryOfOriginId`
                ) || 'N/A'
              }
              isSmallScreen={false}
            />
          </Grid>
        </DefaultSpacingGrid>
      </Box>
    )
  }

  return (
    <>
      {ModalComponent}
      <Box
        sx={{
          border: '1px solid #ccc',
          padding: '20px 16px 16px 16px',
          marginTop: '16px',
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette?.accordion?.parent?.bg,
        }}
      >
        <DefaultSpacingGrid container>
          {useProduct ? (
            <>
              <Grid item xs={12} md={3}>
                <FormSelect
                  name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productId`}
                  label={formatMessage(messages.product)}
                  options={productOptions}
                  placeholder={formatMessage(commonMessages.select)}
                  required={true}
                  actionLabel={
                    productsCount > 1 && (
                      <ActionButton
                        onClick={() => {
                          removeProduct(productIndex)
                        }}
                        label={formatMessage(messages.removeProduct)}
                        color='error'
                      />
                    )
                  }
                  onChange={(e) => {
                    const productPrefix = `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}`
                    const existingPhysicalPropertiesId = getValues(`${productPrefix}.physicalPropertiesId`)
                    const existingTemperatureSettingId = getValues(`${productPrefix}.temperatureSettingId`)

                    setValue(`${productPrefix}.physicalProperties`, null)
                    setValue(`${productPrefix}.physicalPropertiesId`, existingPhysicalPropertiesId ?? undefined)
                    setValue(`${productPrefix}.temperatureSetting`, undefined)
                    setValue(`${productPrefix}.temperatureSettingId`, existingTemperatureSettingId ?? undefined)
                    setValue(`${productPrefix}.temperatureControlled`, false)
                    setValue(`${productPrefix}.hsHeadingId`, undefined)
                    setValue(`${productPrefix}.hsSubheadingId`, undefined)

                    fetchProduct({ tenantId, customerId, productId: +e.target.value }).then(
                      (res) => {
                        if (res.data) {
                          const selectedProduct = res.data
                          const activePrice = getActivePriceInfo(selectedProduct.prices)
                          setValue(
                            `${productPrefix}.unitPrice`,
                            activePrice?.price ?? 0
                          )

                          setValue(
                            `${productPrefix}.countryOfOriginId`,
                            selectedProduct.countryOfOriginId
                          )
                          setValue(
                            `${productPrefix}.productName`,
                            selectedProduct.name
                          )
                          setValue(
                            `${productPrefix}.productDescription`,
                            selectedProduct.name
                          )
                          setValue(
                            `${productPrefix}.temperatureControlled`,
                            selectedProduct.temperatureSetting ? true : false
                          )

                          if (selectedProduct.physicalProperties) {
                            const pp = selectedProduct.physicalProperties
                            const { length, width, height } = pp
                            setValue(
                              `${productPrefix}.physicalPropertiesId`,
                              existingPhysicalPropertiesId ?? undefined
                            )
                            setValue(
                              `${productPrefix}.physicalProperties`,
                              {
                                id: existingPhysicalPropertiesId ?? undefined,
                                netWeight: pp.netWeight ?? null,
                                grossWeight: pp.grossWeight ?? null,
                                tareWeight: pp.tareWeight ?? null,
                                weightMeasureUnitId: pp.weightMeasureUnitId ?? null,
                                length: pp.length ?? null,
                                width: pp.width ?? null,
                                height: pp.height ?? null,
                                lengthMeasureUnitId: pp.lengthMeasureUnitId ?? null,
                                volume: pp.volume ?? null,
                                volumeMeasureUnitId: pp.volumeMeasureUnitId ?? null,
                                density: pp.density ?? null,
                                densityMeasureUnitId: pp.densityMeasureUnitId ?? null,
                                useDimensions: !!(length || width || height),
                                isRectangular: width !== undefined && width !== null,
                              }
                            )
                          } else {
                            setValue(
                              `${productPrefix}.physicalProperties`,
                              {
                                id: existingPhysicalPropertiesId ?? undefined,
                                netWeight: null,
                                grossWeight: null,
                                tareWeight: null,
                                weightMeasureUnitId: null,
                                length: null,
                                width: null,
                                height: null,
                                lengthMeasureUnitId: null,
                                volume: null,
                                volumeMeasureUnitId: null,
                                density: null,
                                densityMeasureUnitId: null,
                                useDimensions: false,
                                isRectangular: false,
                              }
                            )
                          }

                          if (selectedProduct.temperatureSetting) {
                            const ts = selectedProduct.temperatureSetting
                            setValue(
                              `${productPrefix}.temperatureSettingId`,
                              existingTemperatureSettingId ?? undefined
                            )
                            setValue(
                              `${productPrefix}.temperatureSetting`,
                              {
                                id: existingTemperatureSettingId ?? undefined,
                                setPoint: ts.setPoint ?? null,
                                temperatureRangeId: ts.temperatureRangeId ?? undefined,
                                temperatureUnitId: ts.temperatureUnitId ?? null,
                                upperThresholdWarning: ts.upperThresholdWarning ?? null,
                                upperThresholdCritical: ts.upperThresholdCritical ?? null,
                                lowerThresholdWarning: ts.lowerThresholdWarning ?? null,
                                lowerThresholdCritical: ts.lowerThresholdCritical ?? null,
                              }
                            )
                          } else {
                            setValue(
                              `${productPrefix}.temperatureSetting`,
                              {
                                id: existingTemperatureSettingId ?? undefined,
                                setPoint: null,
                                temperatureRangeId: undefined,
                                temperatureUnitId: null,
                                upperThresholdWarning: null,
                                upperThresholdCritical: null,
                                lowerThresholdWarning: null,
                                lowerThresholdCritical: null,
                              }
                            )
                          }

                          if (selectedProduct.productCategory?.hsHeadingId) {
                            setValue(
                              `${productPrefix}.hsHeadingId`,
                              selectedProduct.productCategory.hsHeadingId
                            )
                          }
                          if (selectedProduct.productCategory?.hsSubheadingId) {
                            setValue(
                              `${productPrefix}.hsSubheadingId`,
                              selectedProduct.productCategory.hsSubheadingId
                            )
                          }
                        }
                      }
                    )
                  }}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={3}>
              <FormInput
                name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productDescription`}
                label={formatMessage(messages.productDescription)}
                placeholder={formatMessage(commonMessages.pleaseEnter)}
                maxLength={200}
                required={true}
                actionLabel={
                  productsCount > 1 && (
                    <ActionButton
                      onClick={() => {
                        removeProduct(productIndex)
                      }}
                      color='error'
                      label={formatMessage(messages.removeProduct)}
                    />
                  )
                }
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <FormSelect
              name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.countryOfOriginId`}
              label={formatMessage(messages.countryOfOrigin)}
              options={convertToLabelValue(countries)}
              required={isCountryOfOriginRequired}
              placeholder={formatMessage(commonMessages.select)}
              disabled={useProduct}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <FormInput
              name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.quantity`}
              label={formatMessage(messages.quantity)}
              type='number'
              required={true}
              numberFormatProps={{
                allowOnlyIntegers: true,
                minValue: 1,
              }}
              defaultValue={1}
              fullWidth
              maxLength={13}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormInput
              name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.unitPrice`}
              type='number'
              label={formatMessage(messages.unitPrice)}
              disabled={useProduct}
              required={true}
              numberFormatProps={{
                decimalScale: 2,
              }}
              unit={currencies.find((currency) => currency.id === currencyId)?.localisedSymbol}
              maxLength={22}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplay
              label={formatMessage(messages.totalValue)}
              value={total}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
              }}
              unit={currencies.find((currency) => currency.id === currencyId)?.localisedSymbol}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Controller
                name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.useProduct`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}`,
                            null
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.countryOfOriginId`,
                            null
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productId`,
                            null
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.productDescription`,
                            null
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.unitPrice`,
                            0
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.quantity`,
                            1
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.physicalProperties`,
                            null
                          )
                          setValue(
                            `bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}.temperatureSetting`,
                            null
                          )
                          field.onChange(e)
                        }}
                      />
                    }
                    label={formatMessage(messages.selectProduct)}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <LoadContentTabs
              prefix={`bookings.${consignmentIndex}.packaging.${packagingIndex}.products.${productIndex}`}
              bookingIndex={consignmentIndex}
              disableAllExceptBatch={useProduct}
            />
          </Grid>
        </DefaultSpacingGrid>
      </Box>
    </>
  )
}

export default ProductForm
